import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Grid, TextField, Button, Typography, Divider, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, Autocomplete, Stack,
  IconButton, Tooltip, Snackbar, Alert, Tabs, Tab, MenuItem, LinearProgress, Collapse,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import api from "../services/api";
import { getUser } from "../services/auth";
import { todayISO } from "../utils/date";

const isAdmin = () => getUser()?.role === "admin";
const num = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const today = () => todayISO();

export default function WireChopping() {
  const [products, setProducts] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState("jobs"); // jobs | boxes
  const [toast, setToast] = useState(null); // {type, msg}

  const notify = (type, msg) => setToast({ type, msg });

  const loadProducts = useCallback(() => {
    api.get("/wireChopping/products").then((r) => setProducts(r.data || [])).catch(() => {});
  }, []);

  const loadJobs = useCallback(() => {
    api.get("/wireChopping", { params: { status: statusFilter || undefined, search: search || undefined } })
      .then((r) => setJobs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setJobs([]));
  }, [statusFilter, search]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const deleteJob = (j) => {
    if (!window.confirm(`Delete chop job ${j.JobNo} (${j.Wire_Item_Description})?\nThis removes the job and restores the wire to stock. Only open jobs can be deleted.`)) return;
    api.delete(`/wireChopping/${j.Master_ID}`)
      .then(() => { notify("success", `${j.JobNo} deleted`); loadJobs(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to delete job"));
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>Wire Chopping</Typography>

      <Tabs value={view} onChange={(_e, v) => setView(v)} sx={{ mb: 2 }}>
        <Tab value="jobs" label="Chop Jobs" />
        <Tab value="boxes" label="CU Boxes" />
      </Tabs>

      {view === "boxes" && <OpenBoxesPanel notify={notify} />}

      {view === "jobs" && !openId && (
        <>
          <StartJobForm products={products} onStarted={(id) => { loadJobs(); setOpenId(id); }} notify={notify} />

          <Paper sx={{ p: 2, mt: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
              <TextField select SelectProps={{ native: true }} label="Status" size="small"
                InputLabelProps={{ shrink: true }}
                value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
                <option value="">All</option>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </TextField>
              <TextField label="Search wire / job #" size="small" value={search}
                onChange={(e) => setSearch(e.target.value)} />
            </Stack>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job #</TableCell><TableCell>Date</TableCell><TableCell>Wire</TableCell>
                    <TableCell align="right">Input</TableCell><TableCell align="right">Exp. CU</TableCell>
                    <TableCell align="right">Collected</TableCell><TableCell align="right">Variance</TableCell>
                    <TableCell align="right">Recovery</TableCell><TableCell>Status</TableCell><TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.map((j) => {
                    const exp = (Number(j.Input_Qty) || 0) * (Number(j.Expected_Recovery_Pct) || 0) / 100;
                    const variance = (Number(j.CollectedQty) || 0) - exp;
                    const rec = Number(j.Input_Qty) > 0 ? (Number(j.CollectedQty) / Number(j.Input_Qty)) * 100 : 0;
                    return (
                      <TableRow key={j.Master_ID} hover>
                        <TableCell>{j.JobNo}</TableCell>
                        <TableCell>{(j.Date || "").slice(0, 10)}</TableCell>
                        <TableCell>{j.Wire_Item_Description}</TableCell>
                        <TableCell align="right">{num(j.Input_Qty)}</TableCell>
                        <TableCell align="right">{num(exp)}</TableCell>
                        <TableCell align="right">{num(j.CollectedQty)}</TableCell>
                        <TableCell align="right" style={{ color: variance < 0 ? "#c62828" : "inherit" }}>{num(variance)}</TableCell>
                        <TableCell align="right">{rec.toFixed(1)}%</TableCell>
                        <TableCell>
                          <Chip size="small" label={j.Status} color={j.Status === "Open" ? "warning" : "success"} />
                        </TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => setOpenId(j.Master_ID)}>Open</Button>
                          {j.Status === "Open" && (
                            <Tooltip title="Delete job">
                              <IconButton size="small" color="error" onClick={() => deleteJob(j)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!jobs.length && (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ color: "text.secondary" }}>No chop jobs yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </>
      )}

      {view === "jobs" && openId && (
        <JobPanel
          jobId={openId} products={products}
          onBack={() => { setOpenId(null); loadJobs(); }}
          notify={notify}
        />
      )}

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.type} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}

/* ---------------- Start a new job ---------------- */
function StartJobForm({ products, onStarted, notify }) {
  const [wire, setWire] = useState(null);
  const [date, setDate] = useState(today());
  const [inputQty, setInputQty] = useState("");
  const [pct, setPct] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Pre-fill expected % from the wire's saved default.
  useEffect(() => {
    if (!wire) return;
    api.get(`/wireChopping/recovery/${wire.id}`)
      .then((r) => { if (r.data && r.data.Expected_Pct != null) setPct(String(r.data.Expected_Pct)); })
      .catch(() => {});
  }, [wire]);

  const expectedCU = (Number(inputQty) || 0) * (Number(pct) || 0) / 100;

  const start = () => {
    if (!wire) return notify("warning", "Pick the wire being chopped");
    if (!(Number(inputQty) > 0)) return notify("warning", "Enter the input quantity");
    setSaving(true);
    api.post("/wireChopping", {
      Wire_Item_ID: wire.id, Wire_Item_Description: wire.Product_Description,
      Date: date, Input_Qty: Number(inputQty),
      Expected_Recovery_Pct: pct === "" ? null : Number(pct), Notes: notes,
    })
      .then((r) => { notify("success", `${r.data.JobNo} started`); onStarted(r.data.Master_ID); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to start job"))
      .finally(() => setSaving(false));
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Start a Chop Job</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Autocomplete fullWidth options={products} getOptionLabel={(o) => o.Product_Description || ""}
            value={wire} onChange={(_e, v) => setWire(v)}
            renderInput={(p) => <TextField {...p} label="Wire (input)" size="small" fullWidth />} />
        </Grid>
        <Grid item xs={6} sm={2}><TextField type="date" label="Date" size="small" fullWidth
          InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} /></Grid>
        <Grid item xs={6} sm={2}><TextField type="number" label="Input qty (lb)" size="small" fullWidth
          value={inputQty} onChange={(e) => setInputQty(e.target.value)} /></Grid>
        <Grid item xs={6} sm={2}><TextField type="number" label="Recovery %" size="small" fullWidth
          value={pct} onChange={(e) => setPct(e.target.value)} helperText="e.g. 88 for MCM" /></Grid>
        <Grid item xs={6} sm={2}>
          <TextField label="Expected CU" size="small" fullWidth value={num(expectedCU)}
            InputProps={{ readOnly: true }} helperText="auto from recovery %" />
        </Grid>
        <Grid item xs={12} sm={8}><TextField label="Notes" size="small" fullWidth
          value={notes} onChange={(e) => setNotes(e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}>
          <Button variant="contained" fullWidth onClick={start} disabled={saving} sx={{ height: "100%" }}>
            Start Job (deduct wire)
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ---------------- Open / view a job ---------------- */
function JobPanel({ jobId, products, onBack, notify }) {
  const [data, setData] = useState(null); // {master, fills, summary}
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api.get(`/wireChopping/${jobId}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Typography sx={{ p: 2 }}>Loading…</Typography>;
  const { master, fills = [], summary } = data;
  const isOpen = master.Status === "Open";

  const close = () => {
    if (!window.confirm("Close this job? You won't be able to add more copper from this run. Boxes stay open and go to Finished Goods when they reach good weight.")) return;
    api.post(`/wireChopping/${jobId}/close`)
      .then(() => { notify("success", "Job closed"); load(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to close job"));
  };

  const removeFill = (f) => {
    api.delete(`/wireChopping/boxes/${f.Box_ID}/fills/${f.Fill_ID}`)
      .then(() => { notify("success", "Removed"); load(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed"));
  };

  return (
    <Box>
      <Button onClick={onBack} sx={{ mb: 1 }}>&larr; Back to jobs</Button>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography variant="h6">{master.JobNo}</Typography>
          <Chip size="small" label={master.Status} color={isOpen ? "warning" : "success"} />
          {isOpen && !editing && (
            <Tooltip title="Edit job">
              <IconButton size="small" onClick={() => setEditing(true)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          {(master.Date || "").slice(0, 10)} · {master.Wire_Item_Description}
        </Typography>
        <Grid container spacing={2}>
          <Metric label="Input wire" value={`${num(master.Input_Qty)} lb`} />
          <Metric label={`Expected CU (${num(master.Expected_Recovery_Pct)}%)`} value={`${num(summary.ExpectedCU)} lb`} />
          <Metric label="CU collected" value={`${num(summary.CollectedQty)} lb`} />
          <Metric label="Variance" value={`${num(summary.Variance)} lb`} color={summary.Variance < 0 ? "#c62828" : "#2e7d32"} />
          <Metric label="Actual recovery" value={`${summary.ActualRecoveryPct}%`} />
          <Metric label="Leftover (plastic/loss)" value={`${num(summary.Leftover)} lb`} />
        </Grid>
      </Paper>

      {isOpen && editing && (
        <EditJobForm
          master={master} products={products}
          onCancel={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
          notify={notify}
        />
      )}

      {isOpen && <AddCopperForm jobId={jobId} products={products} onAdded={load} notify={notify} />}

      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Copper from this run ({fills.length})</Typography>
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Box</TableCell><TableCell>Grade</TableCell>
                <TableCell align="right">Net (lb)</TableCell><TableCell>Time</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {fills.map((f) => (
                <TableRow key={f.Fill_ID}>
                  <TableCell>{f.BoxSticker}</TableCell>
                  <TableCell>{f.Grade}</TableCell>
                  <TableCell align="right">{num(f.NetWt)}</TableCell>
                  <TableCell>{(f.FilledAt || "").replace("T", " ").slice(0, 16)}</TableCell>
                  <TableCell>
                    {isOpen && f.BoxStatus !== "Promoted" && (
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeFill(f)}><DeleteIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!fills.length && (
                <TableRow><TableCell colSpan={5} align="center" sx={{ color: "text.secondary" }}>No copper added in this run yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        {isOpen && (
          <>
            <Divider sx={{ my: 2 }} />
            <Button variant="contained" color="success" onClick={close}>Close Job (done chopping this wire)</Button>
          </>
        )}
      </Paper>
    </Box>
  );
}

function Metric({ label, value, color }) {
  return (
    <Grid item xs={6} sm={4} md={2}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>{label}</Typography>
      <Typography variant="h6" sx={{ color: color || "inherit" }}>{value}</Typography>
    </Grid>
  );
}

/* ---------------- Edit an open job (input wire is admin-only) ---------------- */
function EditJobForm({ master, products, onCancel, onSaved, notify }) {
  const admin = isAdmin();
  const [wire, setWire] = useState(
    () => products.find((p) => p.id === master.Wire_Item_ID) || null
  );
  const [date, setDate] = useState((master.Date || "").slice(0, 10) || today());
  const [inputQty, setInputQty] = useState(String(master.Input_Qty ?? ""));
  const [pct, setPct] = useState(master.Expected_Recovery_Pct == null ? "" : String(master.Expected_Recovery_Pct));
  const [notes, setNotes] = useState(master.Notes || "");
  const [saving, setSaving] = useState(false);

  const expectedCU = (Number(inputQty) || 0) * (Number(pct) || 0) / 100;

  const save = () => {
    // Non-admins can't change the input wire, so fall back to the stored values.
    const payload = admin
      ? {
          Wire_Item_ID: wire ? wire.id : master.Wire_Item_ID,
          Wire_Item_Description: wire ? wire.Product_Description : master.Wire_Item_Description,
          Input_Qty: Number(inputQty) || 0,
        }
      : {
          Wire_Item_ID: master.Wire_Item_ID,
          Wire_Item_Description: master.Wire_Item_Description,
          Input_Qty: Number(master.Input_Qty) || 0,
        };
    if (admin && !(payload.Input_Qty > 0)) return notify("warning", "Enter the input quantity");
    setSaving(true);
    api.put(`/wireChopping/${master.Master_ID}`, {
      ...payload,
      Date: date,
      Expected_Recovery_Pct: pct === "" ? null : Number(pct),
      Notes: notes,
    })
      .then(() => { notify("success", "Job updated"); onSaved(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to update job"))
      .finally(() => setSaving(false));
  };

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Edit Job</Typography>
      {!admin && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
          The input wire and quantity can only be changed by an admin.
        </Typography>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Autocomplete fullWidth options={products} disabled={!admin}
            getOptionLabel={(o) => o.Product_Description || ""}
            value={wire} onChange={(_e, v) => setWire(v)}
            renderInput={(p) => <TextField {...p} label="Wire (input)" size="small" fullWidth />} />
        </Grid>
        <Grid item xs={6} sm={2}><TextField type="date" label="Date" size="small" fullWidth
          InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} /></Grid>
        <Grid item xs={6} sm={2}><TextField type="number" label="Input qty (lb)" size="small" fullWidth
          value={inputQty} onChange={(e) => setInputQty(e.target.value)}
          InputProps={{ readOnly: !admin }} disabled={!admin} /></Grid>
        <Grid item xs={6} sm={2}><TextField type="number" label="Recovery %" size="small" fullWidth
          value={pct} onChange={(e) => setPct(e.target.value)} helperText="e.g. 88 for MCM" /></Grid>
        <Grid item xs={6} sm={2}>
          <TextField label="Expected CU" size="small" fullWidth value={num(expectedCU)}
            InputProps={{ readOnly: true }} helperText="auto from recovery %" />
        </Grid>
        <Grid item xs={12} sm={8}><TextField label="Notes" size="small" fullWidth
          value={notes} onChange={(e) => setNotes(e.target.value)} /></Grid>
        <Grid item xs={12} sm={4}>
          <Stack direction="row" spacing={1} sx={{ height: "100%" }}>
            <Button variant="contained" onClick={save} disabled={saving} sx={{ flex: 1 }}>Save</Button>
            <Button variant="outlined" onClick={onCancel} disabled={saving} sx={{ flex: 1 }}>Cancel</Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

/* ---------------- Add copper from this run (new box or top up an open box) ---------------- */
function AddCopperForm({ jobId, products, onAdded, notify }) {
  const [openBoxes, setOpenBoxes] = useState([]);
  const [target, setTarget] = useState("new");   // "new" or a Box_ID
  const [grade, setGrade] = useState(null);       // for a new box
  const [boxTarget, setBoxTarget] = useState(""); // good-weight target for a new box
  const [tare, setTare] = useState("");
  const [weight, setWeight] = useState("");
  const [saving, setSaving] = useState(false);

  const loadBoxes = useCallback(() => {
    api.get("/wireChopping/boxes", { params: { status: "Filling" } })
      .then((r) => setOpenBoxes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setOpenBoxes([]));
  }, []);
  useEffect(() => { loadBoxes(); }, [loadBoxes]);

  const add = () => {
    if (!(Number(weight) > 0)) return notify("warning", "Enter the copper weight");
    setSaving(true);
    const done = () => {
      notify("success", "Copper added");
      setWeight("");
      loadBoxes(); onAdded();
    };
    const fail = (e) => notify("error", e.response?.data?.error || "Failed to add copper");
    if (target === "new") {
      if (!grade) { setSaving(false); return notify("warning", "Pick the CU grade for the new box"); }
      api.post("/wireChopping/boxes", {
        Item_ID: grade.id, Item_Description: grade.Product_Description,
        TargetWt: boxTarget === "" ? null : Number(boxTarget),
        TareWt: Number(tare) || 0,
        Master_ID: jobId, NetWt: Number(weight),
      }).then(() => { setGrade(null); setBoxTarget(""); setTare(""); done(); })
        .catch(fail).finally(() => setSaving(false));
    } else {
      api.post(`/wireChopping/boxes/${target}/fill`, { Master_ID: jobId, NetWt: Number(weight) })
        .then(done).catch(fail).finally(() => setSaving(false));
    }
  };

  const isNew = target === "new";
  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Add Copper from this Run</Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField select fullWidth size="small" label="Box" value={target}
            onChange={(e) => setTarget(e.target.value)}>
            <MenuItem value="new">➕ New box</MenuItem>
            {openBoxes.map((b) => (
              <MenuItem key={b.Box_ID} value={b.Box_ID}>
                {b.StickerCode} · {b.Item_Description} · {num(b.CurrentWt)}{b.TargetWt ? ` / ${num(b.TargetWt)}` : ""} lb
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {isNew && (
          <>
            <Grid item xs={12} sm={4}>
              <Autocomplete options={products} getOptionLabel={(o) => o.Product_Description || ""}
                value={grade} onChange={(_e, v) => setGrade(v)}
                renderInput={(p) => <TextField {...p} label="CU grade (new box)" size="small" />} />
            </Grid>
            <Grid item xs={6} sm={2}><TextField type="number" label="Target (lb)" size="small" fullWidth
              value={boxTarget} onChange={(e) => setBoxTarget(e.target.value)} helperText="good weight (optional)" /></Grid>
            <Grid item xs={6} sm={2}><TextField type="number" label="Tare" size="small" fullWidth
              value={tare} onChange={(e) => setTare(e.target.value)} /></Grid>
          </>
        )}

        <Grid item xs={6} sm={2}><TextField type="number" label="Copper (lb)" size="small" fullWidth
          value={weight} onChange={(e) => setWeight(e.target.value)} /></Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="contained" fullWidth onClick={add} disabled={saving}>
            {isNew ? "Create box" : "Add"}
          </Button>
        </Grid>
      </Grid>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
        Top up an existing open box to continue filling it across jobs (e.g. finish an Ext Cord box with Cat 5 copper).
      </Typography>
    </Paper>
  );
}

/* ---------------- Open CU Boxes (fill across jobs, promote when full) ---------------- */
function OpenBoxesPanel({ notify }) {
  const [boxes, setBoxes] = useState([]);
  const [showPromoted, setShowPromoted] = useState(false);
  const [expanded, setExpanded] = useState(null); // Box_ID whose fills are shown
  const [fills, setFills] = useState([]);

  const load = useCallback(() => {
    api.get("/wireChopping/boxes")
      .then((r) => setBoxes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBoxes([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleExpand = (b) => {
    if (expanded === b.Box_ID) { setExpanded(null); setFills([]); return; }
    setExpanded(b.Box_ID); setFills([]);
    api.get(`/wireChopping/boxes/${b.Box_ID}`).then((r) => setFills(r.data?.fills || [])).catch(() => setFills([]));
  };

  const promote = (b) => {
    if (!window.confirm(`Send box ${b.StickerCode} (${num(b.CurrentWt)} lb ${b.Item_Description}) to Finished Goods?`)) return;
    api.post(`/wireChopping/boxes/${b.Box_ID}/promote`)
      .then((r) => { notify("success", `Sent to Finished Goods (${r.data.FG_Sticker})`); load(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to promote box"));
  };

  const remove = (b) => {
    if (!window.confirm(`Delete empty/incorrect box ${b.StickerCode}? Its fills will be removed and the copper taken back out of stock.`)) return;
    api.delete(`/wireChopping/boxes/${b.Box_ID}`)
      .then(() => { notify("success", "Box deleted"); load(); })
      .catch((e) => notify("error", e.response?.data?.error || "Failed to delete box"));
  };

  const rows = boxes.filter((b) => showPromoted || b.Status !== "Promoted");

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>CU Boxes</Typography>
        <Button size="small" onClick={() => setShowPromoted((s) => !s)}>
          {showPromoted ? "Hide shipped" : "Show shipped"}
        </Button>
      </Stack>
      <Box sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Box</TableCell><TableCell>Grade</TableCell>
              <TableCell align="right">Current</TableCell><TableCell align="right">Target</TableCell>
              <TableCell sx={{ minWidth: 120 }}>Fill</TableCell>
              <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((b) => {
              const pct = b.TargetWt > 0 ? Math.min(100, (Number(b.CurrentWt) / Number(b.TargetWt)) * 100) : 0;
              const isPromoted = b.Status === "Promoted";
              return (
                <React.Fragment key={b.Box_ID}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggleExpand(b)}>
                        <ExpandMoreIcon fontSize="small"
                          sx={{ transform: expanded === b.Box_ID ? "rotate(180deg)" : "none", transition: "0.2s" }} />
                      </IconButton>
                    </TableCell>
                    <TableCell>{b.StickerCode}</TableCell>
                    <TableCell>{b.Item_Description}</TableCell>
                    <TableCell align="right">{num(b.CurrentWt)}</TableCell>
                    <TableCell align="right">{b.TargetWt ? num(b.TargetWt) : "—"}</TableCell>
                    <TableCell>
                      {b.TargetWt > 0
                        ? <LinearProgress variant="determinate" value={pct}
                            color={pct >= 100 ? "success" : "primary"} sx={{ height: 8, borderRadius: 1 }} />
                        : <Typography variant="caption" sx={{ color: "text.secondary" }}>no target</Typography>}
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={b.Status}
                        color={isPromoted ? "default" : (b.Status === "Full" ? "success" : "warning")} />
                    </TableCell>
                    <TableCell align="right">
                      {!isPromoted && (
                        <>
                          <Button size="small" variant="contained" color="success" onClick={() => promote(b)}
                            disabled={!(Number(b.CurrentWt) > 0)}>
                            Send to FG
                          </Button>
                          <Tooltip title="Delete box">
                            <IconButton size="small" color="error" onClick={() => remove(b)}><DeleteIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </>
                      )}
                      {isPromoted && <Typography variant="caption" sx={{ color: "text.secondary" }}>FG #{b.Generated_Box_ID}</Typography>}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} sx={{ py: 0, border: 0 }}>
                      <Collapse in={expanded === b.Box_ID} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1, mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Fills (source wires)</Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Job</TableCell><TableCell>Source wire</TableCell>
                                <TableCell align="right">Net (lb)</TableCell><TableCell>Time</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {fills.map((f) => (
                                <TableRow key={f.Fill_ID}>
                                  <TableCell>{f.JobNo || "—"}</TableCell>
                                  <TableCell>{f.Wire_Item_Description || "—"}</TableCell>
                                  <TableCell align="right">{num(f.NetWt)}</TableCell>
                                  <TableCell>{(f.FilledAt || "").replace("T", " ").slice(0, 16)}</TableCell>
                                </TableRow>
                              ))}
                              {!fills.length && (
                                <TableRow><TableCell colSpan={4} align="center" sx={{ color: "text.secondary" }}>No fills.</TableCell></TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {!rows.length && (
              <TableRow><TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>No open boxes. Boxes are created when you add copper inside a chop job.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
}
